import { useCallback } from 'react';
import { useInsuranceForm } from '../../../../hooks/formHook/useInsuranceForm';
import { FormOptions } from '../../../../hooks/useFormHookFactory/useFormHookFactory';
import FormItem from '../../../../uiComponent/FormItem/FormItem';
import { PriorAuthApprovalFormItemComponent } from '../PriorAuthApprovalFormItemComponent/PriorAuthApprovalFormItemComponent';
import { PriorAuthDeniedFormItemComponent } from '../PriorAuthDeniedFormItemComponent/PriorAuthDeniedFormItemComponent';
import { PriorAuthStatusSelectComponent } from '../PriorAuthStatusSelectComponent/PriorAuthStatusSelectComponent';

import './PriorAuthCardFormItemComponent.scss';
import { PriorAuthSubmissionNoteComponent } from '../PriorAuthLayout/PriorAuthSubmissionNoteComponent';
import { PriorAuthCaseStatusEnum } from '../../../../uc-api-sdk';
import { PriorAuthViewCardComponent } from '../PriorAuthLayout/PriorAuthViewCardComponent';
import { CPTCodeFormItemComponent } from '../CPTCodeFormItemComponent/CPTCodeFormItemComponent';
import { PriorAuthCommentFormItemComponent } from '../PriorAuthCommentFormItemComponent/PriorAuthCommentFormItemComponent';
import { PriorAuthPendingFormItemComponent } from '../PriorAuthPendingFormItemComponent/PriorAuthPendingFormItemComponent';

export interface PriorAuthCardFormItemComponentProps {
  form?: FormOptions['form'];
  fieldName?: number;
}

export const PriorAuthCardFormItemComponent = ({
  form,
  fieldName,
}: PriorAuthCardFormItemComponentProps) => {
  const insuranceForm = useInsuranceForm({ form });

  const renderFormItem = useCallback((
    status: PriorAuthCaseStatusEnum,
  ) => {
    switch (status) {
      case PriorAuthCaseStatusEnum.NEED_SUBMISSION:
        return (
          <>
            <CPTCodeFormItemComponent fieldName={fieldName} />
            <PriorAuthCommentFormItemComponent fieldName={fieldName} />
            <PriorAuthSubmissionNoteComponent alwaysShow />
          </>
        );
      case PriorAuthCaseStatusEnum.PENDING_RESULT:
        return (
          <>
            <PriorAuthPendingFormItemComponent fieldName={fieldName} />
            <PriorAuthCommentFormItemComponent fieldName={fieldName} />
          </>
        );
      case PriorAuthCaseStatusEnum.APPROVED:
        return (
          <>
            <PriorAuthApprovalFormItemComponent fieldName={fieldName} />
            <CPTCodeFormItemComponent fieldName={fieldName} />
            <PriorAuthCommentFormItemComponent fieldName={fieldName} />
          </>
        );
      case PriorAuthCaseStatusEnum.DENIED:
        return (
          <>
            <PriorAuthDeniedFormItemComponent fieldName={fieldName} />
            <PriorAuthCommentFormItemComponent fieldName={fieldName} />
          </>
        );
      default:
        return null;
    }
  }, []);

  return (
    <div className="prior-auth-card-form white-card">
      <FormItem
        noStyle
        shouldUpdate={insuranceForm.shouldUpdateFromList(['priorAuthActiveCase'], fieldName)}
      >
        {
          ({ getFieldValue }) => {
            const shouldHide = insuranceForm.doesHaveReadOnlyActiveCase(getFieldValue, fieldName);
            const isDisabled = insuranceForm.shouldDisablePriorAuthStatus(getFieldValue, fieldName);
            return (
              <FormItem
                name={insuranceForm.makeListNamePath('priorAuthStatus', fieldName)}
                label={insuranceForm.getLabel('priorAuthStatus')}
                required
                hidden={shouldHide}
              >
                <PriorAuthStatusSelectComponent
                  disabled={isDisabled}
                />
              </FormItem>
            );
          }
        }
      </FormItem>
      <FormItem
        noStyle
        shouldUpdate={(
          insuranceForm.shouldUpdateFromList(
            ['priorAuthStatus', 'priorAuthActiveCase'],
            fieldName
          )
        )}
      >
        {
          ({ getFieldValue }) => {
            const priorAuthStatus = insuranceForm.getObjectValue('priorAuthStatus', getFieldValue, fieldName);
            const priorAuthActiveCase = insuranceForm.getObjectValue('priorAuthActiveCase', getFieldValue, fieldName);
            if (
              priorAuthActiveCase
              && ([
                PriorAuthCaseStatusEnum.APPROVED,
                PriorAuthCaseStatusEnum.DENIED
              ].includes(priorAuthActiveCase?.status || '' as PriorAuthCaseStatusEnum))
            ) {
              return (
                <PriorAuthViewCardComponent
                  priorAuth={priorAuthActiveCase}
                />
              );
            }
            return renderFormItem(priorAuthStatus);
          }
        }
      </FormItem>
    </div>
  );
};
